// The FINANCE console route subtree (manual-payments slice). Code-based routes
// per DEV-GUIDE-002 §4 — this factory hangs the `/finance` tree off the
// portal's root route so router.tsx stays the single wiring point (composed
// ALONGSIDE the LIB/CMS subtrees — none touches another).
//
// GATING: the FINANCE layout route admits any holder of a FIN console
// capability (FIN_CONSOLE_PERMS, owned by modules/fin's schemas so the menu
// and the gate can't drift apart) via ConsoleLayout — anonymous → /login,
// signed-in-without-any-FIN-grant → an explicit denied panel, never a blank
// Outlet. Each destination's API tier is the authority (ADR-001 §14.2): the
// UI can only HIDE. Verification additionally needs VERIFIER STANDING
// (fin:verify OR module :desk/:admin, resolved in-service) — that route is
// deliberately LOADER-FREE (the budget-lines precedent: a loader stricter
// than the layout gate would 403 the navigation for a fin:read-only session);
// the screen owns its queries and renders the 403 as an explicit panel.
import { Outlet, createRoute } from "@tanstack/solid-router";
import { lazyRoute } from "@erp/ui/route-kit";
import { lazy } from "solid-js";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { panelSearch } from "@erp/ui/side-panel";
import type { PanelSearch } from "@erp/ui/side-panel";
import { finQueries } from "../index.js";
import { FIN_CONSOLE_PERMS, InvoiceStatus, VerificationTab } from "../../schemas/index.js";
import type { AnyRoute } from "@tanstack/solid-router";

export const INVOICES_PAGE_SIZE = 20;
export const VERIFICATION_PAGE_SIZE = 20;
export const LEDGER_PAGE_SIZE = 25;
/**
 * The Overview KPIs aggregate over the FIRST keyset page of due/partial
 * invoices at the wire's 100-row cap (CursorQuery max). A page that still has
 * a nextCursor renders its figure as "100+" — real data, honestly truncated,
 * never a mock (fin has no invoice-stats endpoint in v1).
 */
export const OVERVIEW_SCAN_LIMIT = 100;

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/* ───────────────────────── InvoicesList URL-as-state ─────────────────────────
 * The keyset register's facets (?status/?module/?search) live in the URL (the
 * AnnouncementsList precedent). Self-healing per DEV-GUIDE-002 §4.1: the
 * imported InvoiceStatus Zod enum drives `.catch(undefined)` so a garbage
 * `?status=` heals to "no filter"; ?module is the loose cross-module vocabulary
 * ('lib'/'general'/…) and ?search matches invoice_no / payer_name — both plain
 * strings, healed by presence check alone.
 *
 * NO ?cursor (2026-09-22). The register STREAMS: pages accumulate in one
 * `useInfiniteQuery` entry, so a cursor is a position inside the stream rather
 * than a view of it, and it has left the URL, the deps and the loader alike. A
 * stale `?cursor=` on an old bookmark falls out of this validator and reaches
 * neither the stream's filter object nor its first page — the register opens at
 * the newest charge with every facet restored. */
export interface InvoicesSearch extends PanelSearch {
  status?: InvoiceStatus;
  module?: string;
  search?: string;
}

const invoiceStatusParam = InvoiceStatus.optional().catch(undefined);

/** The closed set of panels /finance/invoices may open (create is the only form). */
export const INVOICE_PANELS = ["new"] as const;

export function validateInvoicesSearch(input: Record<string, unknown>): InvoicesSearch {
  return {
    status: invoiceStatusParam.parse(input.status),
    module: optionalString(input.module),
    search: optionalString(input.search),
    ...panelSearch(INVOICE_PANELS, input),
  };
}

/* ─────────────────────── Invoice-detail URL-as-state ───────────────────────
 * The detail screen is otherwise stateless — its ONLY URL state is the side
 * panel, so the whole validator IS the panel contract (the Accounts precedent).
 * "history" is the AUDIT TRAIL (who touched the invoice record), which is a
 * different artefact from the invoice's own append-only money LEDGER rendered
 * on the page — both are labelled so the two can never be read as one. */
export const INVOICE_DETAIL_PANELS = ["history"] as const;

export function validateInvoiceDetailSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(INVOICE_DETAIL_PANELS, input);
}

/* ──────────────────────── Verification URL-as-state ────────────────────────
 * The checker queue's ?tab (pending/verified/declined) + ?module/?search live
 * in the URL. Self-healing: the imported VerificationTab enum
 * drives `.catch(undefined)`; an absent/garbage tab heals to undefined and the
 * screen defaults the effective tab to "pending" (matching the wire's own
 * `.default("pending")`), so the loader-free screen and the URL agree.
 *
 * The PROOF VIEWER is a view, so it lives in the URL too
 * (`?panel=proofs&panelId=<paymentId>`, the staff desk's PAYMENTS_PANELS
 * precedent): a checker can paste a link straight to a claim's evidence, Back
 * closes it, and the queue underneath keeps its tab, module and search.
 * Verify/decline/retry stay MODALS — confirmations, not views.
 *
 * NO ?cursor (2026-09-22) — the queue STREAMS; see the InvoicesSearch note. */
export interface VerificationSearch extends PanelSearch {
  tab?: VerificationTab;
  module?: string;
  search?: string;
}

/** The closed panel set the checker queue may open. */
export const VERIFICATION_PANELS = ["proofs"] as const;

const verificationTabParam = VerificationTab.optional().catch(undefined);

export function validateVerificationSearch(input: Record<string, unknown>): VerificationSearch {
  return {
    tab: verificationTabParam.parse(input.tab),
    module: optionalString(input.module),
    search: optionalString(input.search),
    ...panelSearch(VERIFICATION_PANELS, input),
  };
}

/* ────────────────────────── Accounts URL-as-state ──────────────────────────
 * The instrument registry is an UNPAGED bare list — its only URL state is the
 * side panel (create/edit), so the whole validator IS the panel contract.
 * `?panelId` names the instrument being edited; absent ⇒ create. */
export const ACCOUNT_PANELS = ["new", "edit"] as const;

export function validateAccountsSearch(input: Record<string, unknown>): PanelSearch {
  return panelSearch(ACCOUNT_PANELS, input);
}

/* ─────────────────────────── Ledger URL-as-state ───────────────────────────
 * The append-only journal narrows by ?invoiceId (an opaque id — presence check
 * heals it).
 *
 * NO ?cursor (2026-09-22) — the journal STREAMS; see the InvoicesSearch note. */
export interface LedgerSearch {
  invoiceId?: string;
}

export function validateLedgerSearch(input: Record<string, unknown>): LedgerSearch {
  return {
    invoiceId: optionalString(input.invoiceId),
  };
}

/* ─────────────────────── Record-payment URL-as-state ───────────────────────
 * The desk screen accepts an optional ?invoice=<invoice_no> prefill so the
 * invoice detail's "Record offline payment" action lands with the lookup
 * pre-run. Plain string, presence-healed. */
export interface RecordPaymentSearch {
  invoice?: string;
}

export function validateRecordPaymentSearch(input: Record<string, unknown>): RecordPaymentSearch {
  return {
    invoice: optionalString(input.invoice),
  };
}

// The index screen rides solid `lazy()` (the /library landing precedent — a
// nested INDEX route's createLazyRoute id is ambiguous, so the component
// carries the split point instead).
const FinanceOverviewLazy = lazy(() => import("../screens/admin/Overview.js").then((m) => ({ default: m.FinanceOverview })));

/**
 * The FINANCE shell: admits any FIN capability holder, then renders the active
 * child (the CmsLayout precedent — an explicit denied panel, never a blank
 * Outlet).
 */
function FinanceLayout() {
  return (
    <ConsoleLayout
      perm={FIN_CONSOLE_PERMS}
      deniedMessage="This account cannot open finance. Ask an administrator for finance access."
      skeletonId="fin-skeleton"
      deniedId="fin-denied"
      unreachableId="fin-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/**
 * Build the `/finance` subtree under the portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Screens
 * close their own `.lazy()` loop with `createLazyRoute('/finance/<x>')`.
 */
export function makeFinanceRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const financeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "finance",
    component: FinanceLayout,
  });

  // `/finance` — the Overview KPIs. The loader warms the SAME cache entries the
  // screen's useQuery re-keys (§4.3); verification/stats 403s for a fin:read-only
  // session (empty verifier scope ⇒ FIN_VERIFIER_FORBIDDEN) and the screen
  // renders what loaded and placeholders what didn't.
  //
  // WARM, NOT AWAITED (audit UX-01, every loader in this file and in staff.tsx /
  // student.tsx). A loader that returns `ensureQueryData(...)` holds the route
  // until the API answers: on a full load the outlet is EMPTY for the whole wait
  // (no pendingComponent is set, so the router's fallback is nothing) and on a
  // sidebar click the OLD screen stays until the new rows load — measured at
  // +2993 ms heading=data on /finance/verification behind a 3 s API delay.
  // `void prefetchQuery(...)` starts the same fetch (same key, same staleTime,
  // deduped against the screen's own read, still fired by `defaultPreload:
  // "intent"` on hover) and returns at once, so the route commits and the
  // screen paints its heading and its own bones from `queryState(...).pending()`.
  // prefetchQuery never throws — the `.catch(() => undefined)` guards the old
  // ensures needed are gone with them; a failure lands in the cache entry the
  // screen's ErrorCard reads.
  const indexRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "/",
    component: () => <FinanceOverviewLazy />,
    loader: ({ context }) => {
      void context.queryClient.prefetchQuery(finQueries.verificationStats());
      void context.queryClient.prefetchQuery(finQueries.invoicesList({ limit: OVERVIEW_SCAN_LIMIT, status: "due" }));
      void context.queryClient.prefetchQuery(finQueries.invoicesList({ limit: OVERVIEW_SCAN_LIMIT, status: "partial" }));
    },
  });

  // The invoice register — the URL owns the facets; ONE
  // finQueries.invoicesListInfinite definition serves loader AND screen (§4.3 —
  // the filters object here MUST equal the screen's so both hit one cache
  // entry). The loader warms the FIRST PAGE of the stream only; every page after
  // it is fetched by the tail's sentinel.
  const invoicesRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "invoices",
    validateSearch: validateInvoicesSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({
      status: search.status,
      module: search.module,
      search: search.search,
    }),
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        finQueries.invoicesListInfinite({
          limit: INVOICES_PAGE_SIZE,
          status: deps.status,
          module: deps.module,
          search: deps.search,
        }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/admin/Invoices.js")));

  // The invoice detail (payments + ledger + void + the record's audit trail).
  // LOADER-FREE like the PO detail — the screen owns its useQuery and renders
  // its own skeleton; ?panel=history is a deep link into the trail panel.
  const invoiceDetailRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "invoices/$invoiceId",
    validateSearch: validateInvoiceDetailSearch, // the side-panel contract (§4.1)
  }).lazy(lazyRoute(() => import("../screens/admin/InvoiceDetail.js")));

  // The checker queue. LOADER-FREE on purpose (see the header comment): its
  // API tier is verifier standing, STRICTER than this layout's any-FIN gate.
  const verificationRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "verification",
    validateSearch: validateVerificationSearch, // self-healing (§4.1)
  }).lazy(lazyRoute(() => import("../screens/admin/Verification.js")));

  // Receiving instruments (config registry — an UNPAGED bare list; GET rides
  // fin:read so the warm-up is safe under the layout gate; writes re-check
  // fin:manage at the API).
  const accountsRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "accounts",
    validateSearch: validateAccountsSearch, // the side-panel contract (§4.1)
    loader: ({ context }) => {
      void context.queryClient.prefetchQuery(finQueries.accountsList());
    },
  }).lazy(lazyRoute(() => import("../screens/admin/Accounts.js")));

  // The append-only journal (fin:read).
  const ledgerRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "ledger",
    validateSearch: validateLedgerSearch, // self-healing (§4.1)
    loaderDeps: ({ search }) => ({ invoiceId: search.invoiceId }),
    // The FIRST PAGE of the stream only; every page after it is fetched by the
    // tail's sentinel.
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchInfiniteQuery(
        finQueries.ledgerListInfinite({ limit: LEDGER_PAGE_SIZE, invoiceId: deps.invoiceId }),
      );
    },
  }).lazy(lazyRoute(() => import("../screens/admin/Ledger.js")));

  // The offline-payment desk (record → a DIFFERENT checker verifies). No nav
  // entry — reached from Overview / the invoice detail (a route without a menu
  // item is legitimate, nav.ts header note). Loader-free: the lookup is
  // demand-driven by the operator's typed invoice number.
  const recordRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "record",
    validateSearch: validateRecordPaymentSearch, // self-healing (§4.1)
  }).lazy(lazyRoute(() => import("../screens/admin/RecordPayment.js")));

  // THE CASH DESK (ADR-008 §1) — money the institution physically holds, born
  // `paid` with no queue. Distinct from `record` above, which files a payer's
  // CLAIM for someone else to check. Same self-healing search validator: both
  // arrive with an invoice number in the URL from the invoice detail.
  const deskRoute = createRoute({
    getParentRoute: () => financeRoute,
    path: "desk",
    validateSearch: validateRecordPaymentSearch,
  }).lazy(lazyRoute(() => import("../screens/admin/CashierDesk.js")));

  return [
    financeRoute.addChildren([
      indexRoute,
      invoicesRoute,
      invoiceDetailRoute,
      verificationRoute,
      accountsRoute,
      ledgerRoute,
      recordRoute,
      deskRoute,
    ]),
  ];
}
